import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {LogService} from '@app/services/log.service';
import {Store} from '@ngxs/store';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {CommandService} from '@app/services/command.service';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {LogLevel, LogResource} from '@app/modules/shared/data/constants';
import {CoreService} from '@app/services/core.service';
import {WrtcService} from '@app/modules/wrtc/services/wrtc.service';
import {RemoteUserAction} from '@app/state/remote-users.state';

@Component({
  selector: 'app-students-fullscreen-mask',
  templateUrl: './students-full-screen-mask.component.html'
})
export class StudentsFullScreenMaskComponent implements OnInit {
  public classroom: ClassroomStateModel;
  public localUser: LocalUser;
  public isInFullscreenMode: boolean = false;

  constructor(
    private store: Store,
    private commandService: CommandService,
    private coreService: CoreService,
    private wrtcService: WrtcService
  ) {}

  /*
  * ipad users don't have full screen mode, therefore fullscreen mode change handler can't be triggered
  * it means that we can't send 'fullscreen out' event to teacher, if student uses ipad.
  * in many cases 'fullscreen out' event is triggered before page reload
  * that's why we use 'on before unload' event to handle 'fullscreen out' event for ipad users.
  *  */

  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  @HostListener('document:mozfullscreenchange')
  private fullscreenChangeHandler(): void {
    this.isInFullscreenMode = !!document.fullscreenElement;
    this.store.dispatch(new ClassroomAction.ToggleFullScreenMode(this.isInFullscreenMode));
    this.notifyVisibilityChange(this.isInFullscreenMode);
  }

 @HostListener('document:visibilitychange') // detect hide browser tab.
 private visibilityChangeHandlerListener(): void {
   this.visibilityChangeHandler();
 }

  @HostListener('keydown', ['$event'])
  private keyDownListener(event): void {
    this.keydownHandler(event);
  }

  public ngOnInit(): void {
    this.registerStoreChangeListeners();
  }

  private visibilityChangeHandler(): void {
    if (document.visibilityState + '' !== 'prerender' && document.readyState === 'complete' && this.classroom.isInFullScreenMode) {
      if (document.hidden) {
          this.notifyVisibilityChange(false);
      }
      else {
          this.notifyVisibilityChange(true);
      }
    }
  }

  private notifyVisibilityChange(enableFullscreen: boolean) {
    if (enableFullscreen) {
      this.coreService.sendFullScreen().subscribe();
    } else {
      this.coreService.sendFullScreenOut().subscribe();
    }
  }

  private registerStoreChangeListeners(): void {
    this.store.select(ClassroomState).subscribe(roomState => {
      this.classroom = roomState;
    });

    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
  }

  private keydownHandler(event: KeyboardEvent): void {
    switch (event.code) {
      case 'F11':
        LogService.writeLog(`keyDown event. keyCode: ${event.code}`, LogLevel.DEBUG, LogResource.CORE);
        event.preventDefault();
        event.stopPropagation();
        this.toggleFullscreen();
        break;
    }
  }

  public toggleFullscreen(): void {
    CoreService.playTimerNotificationSound(true);
    this.coreService.initApplauseAudio();
    this.wrtcService.playFullScreenAudio(true);
    LogService.writeLog(`toggleFullscreen call`, LogLevel.DEBUG, LogResource.CORE);
    //if the screen is not fullscreen yet
    if (!this.isInFullscreenMode) {
      LogService.writeLog(`toggleFullscreen custom event - to fullscreen`, LogLevel.DEBUG, LogResource.CORE);
      if (this.classroom.isIpadMode) {
        this.coreService.sendFullScreen().subscribe();
      } else {
        document.documentElement.requestFullscreen().then();
      }
      this.store.dispatch(new ClassroomAction.ToggleFullScreenMode(true));
      this.store.dispatch(new RemoteUserAction.SetIsInFullscreenMode(this.localUser.id,false));
      if (this.localUser.lateTimeInMinutes) {
        this.store.dispatch(new LocalUserAction.SetLateTime(-1));
      }
    } else {
      LogService.writeLog(`toggleFullscreen custom event - cancel fullscreen`, LogLevel.DEBUG, LogResource.CORE);
      document.exitFullscreen().then();
    }

    this.isInFullscreenMode = !this.isInFullscreenMode;
  }

}
